import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import SEO, { getBreadcrumbSchema } from '../components/SEO';
import { Package, Search, MapPin, Plus, Filter, ChevronRight, Tag, User, Building2, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MaterialsList = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    location: searchParams.get('location') || '',
    condition: searchParams.get('condition') || '',
    search: searchParams.get('search') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API}/materials/categories`);
        if (res.ok) setCategories(await res.json());
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchMaterials = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.category) params.append('category', filters.category);
        if (filters.location) params.append('location', filters.location);
        if (filters.condition) params.append('condition', filters.condition);
        if (filters.search) params.append('search', filters.search);
        if (filters.min_price) params.append('min_price', filters.min_price);
        if (filters.max_price) params.append('max_price', filters.max_price);
        
        const res = await fetch(`${API}/materials?${params}`);
        if (res.ok) {
          const data = await res.json();
          setMaterials(data.materials || []);
          setTotal(data.total || 0);
        }
      } catch (err) {
        console.error('Error fetching materials:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMaterials();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="materials-list-page">
      <SEO 
        title="Giełda Materiałów Budowlanych"
        description="Kup i sprzedaj materiały budowlane. Cement, drewno, cegły, narzędzia i więcej. Ogłoszenia od osób prywatnych i firm."
        canonical="/materials"
        structuredData={getBreadcrumbSchema([
          { name: 'Strona główna', path: '/' },
          { name: 'Giełda Materiałów', path: '/materials' },
        ])}
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Package className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl text-white">Giełda Materiałów</h1>
              <p className="text-emerald-100">Kup i sprzedaj materiały budowlane</p>
            </div>
          </div>
          
          {isAuthenticated && (
            <Button 
              onClick={() => navigate('/materials/new')} 
              className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold"
              data-testid="add-material-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Dodaj ogłoszenie
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Szukaj materiałów..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={filters.category || "all"} onValueChange={(v) => handleFilterChange('category', v === "all" ? '' : v)}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder="Kategoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie kategorie</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filters.condition || "all"} onValueChange={(v) => handleFilterChange('condition', v === "all" ? '' : v)}>
                <SelectTrigger data-testid="condition-filter">
                  <SelectValue placeholder="Stan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie</SelectItem>
                  <SelectItem value="nowy">Nowy</SelectItem>
                  <SelectItem value="używany">Używany</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Lokalizacja..."
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="pl-10"
                  data-testid="location-input"
                />
              </div>
            </div>
            
            {/* Price filters */}
            <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <DollarSign className="w-4 h-4 text-emerald-500" />
                <span className="font-medium">Cena:</span>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="od"
                  value={filters.min_price}
                  onChange={(e) => handleFilterChange('min_price', e.target.value)}
                  className="w-28"
                  min="0"
                  data-testid="min-price-input"
                />
                <span className="text-slate-400">-</span>
                <Input
                  type="number"
                  placeholder="do"
                  value={filters.max_price}
                  onChange={(e) => handleFilterChange('max_price', e.target.value)}
                  className="w-28"
                  min="0"
                  data-testid="max-price-input"
                />
                <span className="text-slate-500 text-sm">PLN</span>
              </div>
              {(filters.min_price || filters.max_price) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    handleFilterChange('min_price', '');
                    handleFilterChange('max_price', '');
                  }}
                  className="text-slate-500 hover:text-slate-700"
                >
                  Wyczyść
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results count */}
        <p className="text-sm text-slate-500 mb-4">
          Znaleziono <strong>{total}</strong> ogłoszeń
        </p>

        {/* Materials Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-slate-500 mt-4">Ładowanie...</p>
          </div>
        ) : materials.length === 0 ? (
          <Card className="text-center py-12">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-lg text-slate-700 mb-2">Brak ogłoszeń</h3>
            <p className="text-slate-500 mb-4">Nie znaleziono materiałów spełniających kryteria</p>
            {isAuthenticated && (
              <Button onClick={() => navigate('/materials/new')} className="bg-emerald-500 hover:bg-emerald-600">
                Dodaj pierwsze ogłoszenie
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {materials.map(material => (
              <Link key={material.material_id} to={`/materials/${material.material_id}`}>
                <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group">
                  <CardContent className="p-0">
                    {/* Image placeholder */}
                    <div className="h-48 bg-slate-100 rounded-t-lg flex items-center justify-center overflow-hidden">
                      {material.images && material.images.length > 0 ? (
                        <img src={material.images[0]} alt={material.title} className="w-full h-full object-cover" />
                      ) : (
                        <Package className="w-16 h-16 text-slate-300" />
                      )}
                    </div>
                    
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-heading font-semibold text-slate-900 group-hover:text-emerald-600 transition-colors line-clamp-2">
                          {material.title}
                        </h3>
                        <Badge variant={material.condition === 'nowy' ? 'default' : 'secondary'} className="flex-shrink-0">
                          {material.condition === 'nowy' ? 'Nowy' : 'Używany'}
                        </Badge>
                      </div>
                      
                      <p className="text-2xl font-bold text-emerald-600 mb-3">
                        {formatPrice(material.price)}
                        {material.price_negotiable && (
                          <span className="text-sm font-normal text-slate-500 ml-2">do negocjacji</span>
                        )}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {material.location || 'Nie podano'}
                        </span>
                        <span className="flex items-center gap-1">
                          {material.seller_type === 'company' ? (
                            <Building2 className="w-3 h-3" />
                          ) : (
                            <User className="w-3 h-3" />
                          )}
                          {material.seller_name}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* CTA for non-authenticated */}
        {!isAuthenticated && (
          <Card className="mt-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
            <CardContent className="p-6 text-center">
              <h3 className="font-heading font-bold text-xl mb-2">Chcesz sprzedać materiały budowlane?</h3>
              <p className="text-emerald-100 mb-4">Zarejestruj się za darmo i dodaj ogłoszenie</p>
              <Button onClick={() => navigate('/register')} className="bg-white text-emerald-700 hover:bg-emerald-50">
                Zarejestruj się
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MaterialsList;
